import type { TenantJourney } from "@/lib/tenantJourney";
import {
  isDepositEditLocked,
  NOTICE_GIVEN_STATUSES,
} from "@/lib/tenantJourney";
import { detectRoomBaseDrift } from "@/lib/room-base-drift";
import { FIELD_LABEL_KEYS, resolveTenantInfoGaps } from "@/lib/tenantInfoGaps";
import { computeMoveOutWindowStatus } from "@/lib/protocol-window";
import {
  EMPTY_SOON_DAYS,
  KEY_PICKUP_SOON_DAYS,
  MOVEOUT_WINDOW_CLOSING_DAYS,
} from "./thresholds";
import { daysUntil } from "./time";
import type { BrainFact, BrainItem, BrainKind, BrainText } from "./types";

export interface BrainBookingRow {
  id: number;
  user_id: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
  move_in_date: string | null;
  move_out_date: string | null;
  /** The contract's earliest possible move-out (contracts auto-prolong). */
  end_date?: string | null;
  move_out_notice_at: string | null;
  contract_signed_at: string | null;
  key_picked_up_at: string | null;
  key_returned_at: string | null;
  monthly_rent: number | null;
  deposit_amount: number | null;
  move_in_reminder_24h_sent_at?: string | null;
  move_in_reminder_36h_sent_at?: string | null;
  move_out_reminder_12h_sent_at?: string | null;
  move_out_reminder_36h_sent_at?: string | null;
  move_out_reminder_48h_sent_at?: string | null;
  move_out_protocol_reopened_at?: string | null;
  /** Day of the month (smallint). */
  rent_due_date?: number | null;
  payment_status?: string | null;
  rooms: {
    id: number;
    title: string | null;
    price: number | null;
    deposit_amount: number | null;
    property_id: number;
    /** The unit's reminder switch (late rent); the property's is the default. */
    apartments?: { rent_reminders_enabled: boolean | null } | null;
    properties: {
      id: number;
      title: string | null;
      address: string | null;
      city: string | null;
      rent_reminders_enabled?: boolean | null;
    } | null;
  } | null;
  /** The journey resolver reads more columns (contract, discount) than named here. */
  [k: string]: unknown;
}

export interface TenantDeriveInput {
  bookings: BrainBookingRow[];
  journeys: Map<number, TenantJourney>;
  documents: {
    booking_id: number;
    document_type: string;
    file_url: string;
    status: string | null;
  }[];
  profiles: Map<
    string,
    {
      name: string | null;
      email: string | null;
      phone: string | null;
      date_of_birth: string | null;
      gender: string | null;
    }
  >;
  rooms: {
    id: number;
    is_hidden?: boolean | null;
    status?: string | null;
    archived_at?: string | null;
  }[];
  /** room_id → its active booking (useAllActiveOccupants). */
  occupants: Map<number, { bookingId: number }>;
  /** Checkout protocol: booking_id → its pending move_out_date_requests row. */
  moveOutRequests: Map<
    number,
    { id: string; requested_date: string; created_at: string }
  >;
}

const STAGE_KIND: Partial<Record<string, BrainKind>> = {
  signature_pending: "stage_signature_pending",
  deposit_pending: "stage_deposit_pending",
  key_pickup_pending: "stage_key_pickup_pending",
  handover_pending: "stage_handover_pending",
  moveout_scheduled: "stage_moveout_scheduled",
  moveout_protocol_pending: "stage_moveout_protocol_pending",
  key_return_pending: "stage_key_return_pending",
  deposit_refund_pending: "stage_deposit_refund_pending",
};

const TAB_FOR: Partial<Record<BrainKind, string>> = {
  stage_signature_pending: "overview",
  stage_deposit_pending: "payments",
  stage_handover_pending: "movein",
  stage_key_pickup_pending: "movein",
  stage_moveout_scheduled: "moveout",
  stage_moveout_protocol_pending: "moveout",
  stage_key_return_pending: "moveout",
  stage_deposit_refund_pending: "moveout",
  application_review: "documents",
  application_incomplete: "documents",
  move_out_requested: "moveout",
};

function latest(...dates: (string | null | undefined)[]): string | null {
  const ts = dates.filter((d): d is string => !!d).sort();
  return ts.length ? ts[ts.length - 1] : null;
}

function sinceFor(kind: BrainKind, b: BrainBookingRow): string | null {
  switch (kind) {
    case "stage_deposit_pending":
    case "stage_key_pickup_pending":
      return b.contract_signed_at ?? b.created_at;
    case "stage_handover_pending":
      return b.key_picked_up_at ?? b.move_in_date ?? b.created_at;
    case "stage_moveout_scheduled":
      return b.move_out_notice_at ?? b.created_at;
    case "stage_moveout_protocol_pending":
    case "stage_key_return_pending":
      return b.move_out_date ?? b.created_at;
    case "stage_deposit_refund_pending":
      return b.key_returned_at ?? b.move_out_date ?? b.created_at;
    case "stage_status_ahead":
      return b.updated_at ?? b.created_at;
    default:
      return b.created_at;
  }
}

/**
 * Tenant profile deep link. The full-page route keeps the Brain return strip;
 * the tenants-list modal fallback hides it, so it is only for bookings with no room.
 */
export function tenantHref(b: BrainBookingRow, tab?: string): string {
  const r = b.rooms;
  if (!r) return `/admin?tab=tenants&bookingId=${b.id}`;
  const base = `/admin/properties/${r.property_id}/rooms/${r.id}/tenants/${b.id}`;
  return tab ? `${base}?tab=${tab}` : base;
}

function base(
  kind: BrainKind,
  b: BrainBookingRow,
  facts: BrainFact[] = []
): BrainItem {
  const r = b.rooms;
  const p = r?.properties;
  return {
    key: `${kind}:${b.id}`,
    kind,
    since: sinceFor(kind, b),
    who: {
      label: [b.name, r?.title].filter(Boolean).join(" · ") || `#${b.id}`,
      href: tenantHref(b),
    },
    where: p
      ? {
          label: [p.title || p.address, p.city].filter(Boolean).join(" · "),
          href: `/admin/properties/${p.id}`,
        }
      : undefined,
    facts,
    primaryHref: tenantHref(b, TAB_FOR[kind]),
    refs: {
      bookingId: b.id,
      userId: b.user_id,
      email: b.email,
      personName: b.name,
      roomId: r?.id,
      propertyId: p?.id,
    },
  };
}

/** One fact, or null when it has nothing to show. */
const f = (
  key: string,
  fallback: string,
  v: {
    value?: string | null;
    date?: string | null;
    parts?: BrainText[];
    eur?: number | null;
  }
): BrainFact | null => {
  const label = { key: `brain.fact.${key}`, fallback };
  if (v.parts?.length) return { label, parts: v.parts };
  if (v.date) return { label, date: v.date };
  if (v.eur != null) return { label, eur: Number(v.eur) };
  return v.value ? { label, value: v.value } : null;
};

/** The statuses resolveTenantJourney can flag as ahead, in the tenant
 * timeline's own words (activityTimeline.status.*). */
const STATUS_TEXT: Record<string, BrainText> = {
  active: { key: "activityTimeline.status.active", fallback: "Active" },
  notice_given: {
    key: "activityTimeline.status.noticeGiven",
    fallback: "Notice Given",
  },
  move_out_pending: {
    key: "activityTimeline.status.moveOutPending",
    fallback: "Move-Out Pending",
  },
};
const facts = (...xs: (BrainFact | null)[]) =>
  xs.filter((x): x is BrainFact => !!x);

/**
 * An application card's booking: still `pending` (the tenants list offers
 * Approve on exactly these) at the applied stage. A confirmed or live booking
 * can sit at "applied" too (draft contract, no contract on file) — approving
 * it again would regress its status.
 */
/** The documents an application needs before it can be reviewed. */
export const REQUIRED_DOCS = ["id_passport", "proof_of_income"] as const;
export type RequiredDoc = (typeof REQUIRED_DOCS)[number];
const DOC_LABEL_KEYS: Record<RequiredDoc, string> = {
  id_passport: "admin.tenants.docTypeIdPassport",
  proof_of_income: "admin.tenants.docTypeProofOfIncome",
};
const DOC_LABELS_EN: Record<RequiredDoc, string> = {
  id_passport: "ID/Passport",
  proof_of_income: "Proof of Income",
};

export function isApplication(
  b: Pick<BrainBookingRow, "status">,
  j: Pick<TenantJourney, "stageId"> | undefined
): boolean {
  return b.status === "pending" && j?.stageId === "applied";
}

export function deriveTenantItems(
  input: TenantDeriveInput,
  now: Date
): BrainItem[] {
  const out: BrainItem[] = [];
  const docsByBooking = new Map<number, TenantDeriveInput["documents"]>();
  for (const d of input.documents) {
    const list = docsByBooking.get(d.booking_id) ?? [];
    list.push(d);
    docsByBooking.set(d.booking_id, list);
  }

  for (const b of input.bookings) {
    const j = input.journeys.get(b.id);
    if (!j || j.isClosed) continue;
    const stage = j.stageId;
    const application = isApplication(b, j);

    // Checkout protocol (Neel Slack DM 2026-10-19): independent of the
    // stage_* ladder — a request can exist while the journey is still
    // Active, so this isn't keyed off STAGE_KIND like the others.
    const moveOutRequest = input.moveOutRequests.get(b.id);
    if (moveOutRequest) {
      const item = base(
        "move_out_requested",
        b,
        facts(
          f("requestedDate", "Requested date", {
            date: moveOutRequest.requested_date,
          }),
          f("earliestPerContract", "Earliest per contract", {
            date: b.end_date ?? null,
          })
        )
      );
      item.since = moveOutRequest.created_at;
      item.moveOutRequest = {
        requestId: moveOutRequest.id,
        requestedDate: moveOutRequest.requested_date,
        earliestDate: b.end_date ?? null,
        existingNoticeAt: b.move_out_notice_at ?? null,
      };
      out.push(item);
    }

    if (application) {
      const docs = docsByBooking.get(b.id) ?? [];
      const has = (t: string) => docs.some((d) => d.document_type === t);
      const missing = REQUIRED_DOCS.filter((d) => !has(d));
      const item = base(
        missing.length ? "application_incomplete" : "application_review",
        b,
        facts(
          missing.length
            ? f("missing", "Missing", {
                parts: missing.map((d) => ({
                  key: DOC_LABEL_KEYS[d],
                  fallback: DOC_LABELS_EN[d],
                })),
              })
            : null,
          f("requestedMoveIn", "Requested move-in", { date: b.move_in_date }),
          f("rent", "Rent", {
            eur: b.monthly_rent,
          })
        )
      );
      if (missing.length) item.missingFields = missing;
      item.stageId = stage;
      item.documents = docs.map((d) => ({
        type: d.document_type,
        fileUrl: d.file_url,
        status: d.status,
      }));
      out.push(item);
    }

    const kind = STAGE_KIND[stage];
    if (kind) {
      let include = true;
      if (kind === "stage_key_pickup_pending") {
        const d = daysUntil(b.move_in_date, now);
        include = d !== null && d <= KEY_PICKUP_SOON_DAYS;
      }
      if (kind === "stage_moveout_protocol_pending") {
        // Same call as the profile's Move-out tab; a reopened window has no deadline.
        const w = computeMoveOutWindowStatus({
          moveOutDate: b.move_out_date,
          moveOutReopenedAt: b.move_out_protocol_reopened_at,
          now: now.getTime(),
        });
        const d = daysUntil(w.closesAt, now);
        include = d !== null && d <= MOVEOUT_WINDOW_CLOSING_DAYS;
      }
      if (include) {
        const item = base(
          kind,
          b,
          facts(
            f("moveIn", "Move-in", { date: b.move_in_date }),
            f("moveOut", "Move-out", { date: b.move_out_date }),
            f("deposit", "Deposit", {
              eur: b.deposit_amount,
            })
          )
        );
        item.stageId = stage;
        if (kind === "stage_handover_pending") {
          item.lastRemindedAt = latest(
            b.move_in_reminder_24h_sent_at,
            b.move_in_reminder_36h_sent_at
          );
        }
        if (kind === "stage_moveout_protocol_pending") {
          item.lastRemindedAt = latest(
            b.move_out_reminder_12h_sent_at,
            b.move_out_reminder_36h_sent_at,
            b.move_out_reminder_48h_sent_at
          );
        }
        out.push(item);
      }
    }

    if (j.statusAhead) {
      const item = base(
        "stage_status_ahead",
        b,
        facts(
          f("status", "Booking says", {
            parts:
              b.status && STATUS_TEXT[b.status] ? [STATUS_TEXT[b.status]] : [],
            value: b.status,
          })
        )
      );
      item.stageId = stage;
      out.push(item);
    }

    const r = b.rooms;
    if (r) {
      const drift = detectRoomBaseDrift({
        bookingRent: b.monthly_rent,
        bookingDeposit: b.deposit_amount,
        roomPrice: r.price,
        roomDeposit: r.deposit_amount,
        depositLocked: isDepositEditLocked(stage),
      });
      if (drift) {
        // Only what drifted: a locked deposit is history, never shown as drift.
        const item = base("room_base_drift", b);
        item.drift = {
          bookingRent: drift.rent?.booking ?? null,
          roomRent: drift.rent?.room ?? null,
          bookingDeposit: drift.deposit?.booking ?? null,
          roomDeposit: drift.deposit?.room ?? null,
        };
        out.push(item);
      }
    }

    // An application's gaps belong to its application card.
    if (application) continue;
    // Same inputs as the tenant list's "Missing personal info" line.
    const profile = b.user_id ? input.profiles.get(b.user_id) : undefined;
    const gaps = resolveTenantInfoGaps({
      name: profile?.name || b.name,
      dateOfBirth: profile?.date_of_birth ?? null,
      email: profile?.email || b.email,
      phone: profile?.phone || b.phone,
    });
    // COL-530: gender lives on the account only and is filled in by staff —
    // never asked of the tenant (this kind is not in EMAIL_KINDS).
    const missing: string[] = [...gaps];
    if (profile && !profile.gender) missing.push("gender");
    if (missing.length) {
      const item = base(
        "tenant_details_missing",
        b,
        facts(
          f("missing", "Missing", {
            parts: missing.map((m) => ({
              key:
                m === "gender"
                  ? "admin.tenantEdit.gender"
                  : (FIELD_LABEL_KEYS[m] ?? m),
              fallback: m,
            })),
          })
        )
      );
      item.missingFields = missing;
      out.push(item);
    }
  }

  // Occupied = an active booking (the occupant map, app-wide occupancy truth)
  // or a tenant serving notice. Either way the room is soon empty only on a
  // CONFIRMED move-out date (never end_date) inside the window. A notice-given
  // date in the past still counts (they left); on an ACTIVE booking it is
  // stale data, not a vacancy.
  const moveOutById = new Map(
    input.bookings.map((b) => [b.id, b.move_out_date])
  );
  const leavingByRoom = new Map<number, string | null>();
  for (const b of input.bookings) {
    if (b.rooms && NOTICE_GIVEN_STATUSES.has(String(b.status)))
      leavingByRoom.set(b.rooms.id, b.move_out_date);
  }
  const soonEmpty = (
    moveOut: string | null | undefined,
    pastCounts: boolean
  ) => {
    const d = daysUntil(moveOut, now);
    return d !== null && (pastCounts || d >= 0) && d <= EMPTY_SOON_DAYS;
  };
  let empty = 0;
  for (const room of input.rooms) {
    // Unpublished, under maintenance or archived: not a vacancy to fill.
    if (room.is_hidden || room.status === "maintenance" || room.archived_at)
      continue;
    const occ = input.occupants.get(room.id);
    if (occ) {
      if (soonEmpty(moveOutById.get(occ.bookingId), false)) empty += 1;
    } else if (leavingByRoom.has(room.id)) {
      if (soonEmpty(leavingByRoom.get(room.id), true)) empty += 1;
    } else {
      empty += 1;
    }
  }
  if (empty > 0) {
    out.push({
      key: "empty_rooms",
      kind: "empty_rooms",
      since: null,
      summary: String(empty),
      facts: [
        {
          label: { key: "brain.fact.rooms", fallback: "Rooms" },
          value: String(empty),
        },
      ],
      primaryHref: "/admin/properties",
      refs: {},
      rollupCount: empty,
    });
  }
  return out;
}
